import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Stack, TextField, PrimaryButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Dropdown, IDropdownOption, Label, Checkbox } from "@fluentui/react";
import { getModifyGroupData, modifyGroup } from "../../../api/admin/api_admin";
import { ModifyGroupResponse } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";

export const ModifyGroup = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const groupCd = searchParams.get('group_cd');
    
    const [data, setData] = useState<ModifyGroupResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const [groupName, setGroupName] = useState<string>("");
    const [ragFlag, setRagFlag] = useState<boolean>(false);
    const [selectedModel, setSelectedModel] = useState<string>("");

    useEffect(() => {
        if (groupCd) {
            fetchData(groupCd);
        }
    }, [groupCd]);

    const fetchData = async (gcd: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await getModifyGroupData(gcd);
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
                setGroupName(result.group.GRP_group_name);
                setRagFlag(result.group.GRP_default_setting_rag_flag);
                setSelectedModel(result.group.GRP_default_setting_model || "");
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!groupCd) return;

        setSubmitting(true);
        setError(null);
        
        // バリデーション
        if (!groupName) {
            setError("グループ名は必須です");
            setSubmitting(false);
            return;
        }
        if (groupName.includes("'")) {
            setError("グループ名にシングルクォーテーションは使用できません");
            setSubmitting(false);
            return;
        }

        try {
            const result = await modifyGroup({
                group_cd: groupCd,
                group_name: groupName,
                check_setting_rag_flag: ragFlag,
                select_model: selectedModel
            });

            if (result.success) {
                navigate("/menu/groups");
            } else {
                setError(result.message || "更新に失敗しました");
            }
        } catch (err) {
            setError("更新中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    const getModelOptions = (): IDropdownOption[] => {
        if (!data) return [];
        return [
            { key: '', text: '選択してください' },
            ...data.models.map((m) => ({
                key: m.id,
                text: m.name
            }))
        ];
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    if (!groupCd || !data) {
        return (
            <Stack tokens={{ childrenGap: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    {error ?? "グループCDが指定されていません"}
                </MessageBar>
                <LinkButton link_to="/menu/groups" text="戻る" />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="グループ変更" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <Stack>
                    <Label>グループCD</Label>
                    <div>{groupCd}</div>
                </Stack>

                <TextField
                    name="group_name"
                    label="グループ名"
                    value={groupName}
                    onChange={(_, newValue) => setGroupName(newValue || "")}
                    required
                    maxLength={100}
                    description="※シングルクォーテーションは使用できません。"
                    autoComplete="off"
                />

                <Checkbox
                    label="ログイン時に文書検索モードを有効にする"
                    checked={ragFlag}
                    onChange={(_, checked) => setRagFlag(checked || false)}
                />

                <Dropdown
                    label="デフォルトモデル選択"
                    options={getModelOptions()}
                    selectedKey={selectedModel}
                    onChange={(_, option) => setSelectedModel((option?.key as string) || "")}
                />

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="登録"
                        onClick={handleSubmit}
                        disabled={submitting || !groupName}
                    />
                    <LinkButton link_to="/menu/groups" text="戻る" />
                </Stack>
            </Stack>
        </Stack>
    );
}